import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare, HandHeart, Megaphone, UserPlus } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import api from "../lib/api";

function setAppBadge(count) {
  const n = Number(count) || 0;
  try {
    if ("setAppBadge" in navigator) {
      if (n > 0) navigator.setAppBadge(n);
      else if (navigator.clearAppBadge) navigator.clearAppBadge();
    }
  } catch { /* ignore */ }
}

const ICON = {
  dispatch: { Icon: MessageSquare, color: "#0d2340" },
  prayer: { Icon: HandHeart, color: "#C9973A" },
  announcement: { Icon: Megaphone, color: "#2E7D5B" },
  partner_joined: { Icon: UserPlus, color: "#2E7D5B" },
};

export default function NotificationBell({ tone = "light" }) {
  const navigate = useNavigate();
  const dark = tone === "dark";
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [baseline, setBaseline] = useState(null); // seen_at at session start — drives per-item "new" dots
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications/feed");
      setItems(data.items || []);
      setUnread(data.unread || 0);
      setAppBadge(data.unread || 0);
      setBaseline((prev) => (prev == null ? (data.seen_at || "1970-01-01T00:00:00+00:00") : prev));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isNew = (it) => baseline != null && (it.created_at || "") > baseline;
  const newCount = items.filter(isNew).length;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      setAppBadge(0);
      try { await api.post("/notifications/seen"); } catch { /* ignore */ }
    }
  };

  const go = (it) => { setOpen(false); navigate(it.path); };

  const rel = (d) => {
    try { return formatDistanceToNowStrict(new Date(d), { addSuffix: true }); } catch { return ""; }
  };

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        data-testid="notif-bell"
        onClick={toggle}
        className={`relative h-10 w-10 rounded-full flex items-center justify-center transition-colors ${dark ? "text-[var(--brand-navy)] hover:bg-black/5" : "text-white hover:bg-white/15"}`}
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span data-testid="notif-badge" className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ${dark ? "ring-[#FAF8F4]" : "ring-[var(--brand-navy)]"}`}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel" className="absolute right-0 mt-2 w-[20rem] max-w-[88vw] rounded-2xl bg-white shadow-2xl border border-black/5 overflow-hidden z-40">
          <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between">
            <div className="font-heading font-bold text-[#0d2340]">Notifications</div>
            {newCount > 0 && (
              <span data-testid="notif-newcount" className="text-[11px] font-bold text-white bg-red-500 rounded-full px-2 py-0.5">{newCount} new</span>
            )}
          </div>
          <div className="max-h-[65vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notif-empty">
                You're all caught up 🎉
              </div>
            ) : (
              items.map((it) => {
                const cfg = ICON[it.type] || ICON.dispatch;
                const Icon = cfg.Icon;
                const fresh = isNew(it);
                return (
                  <button
                    key={`${it.type}-${it.id}`}
                    data-testid={`notif-item-${it.id}`}
                    onClick={() => go(it)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-black/5 last:border-0 ${fresh ? "bg-[var(--brand-gold)]/[0.08]" : "hover:bg-[#0d2340]/[0.04]"}`}
                  >
                    <span className="relative h-9 w-9 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${cfg.color}18` }}>
                      <Icon className="h-4.5 w-4.5" style={{ color: cfg.color }} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-[#0d2340] truncate">{it.title}</span>
                        {fresh && <span data-testid="notif-item-dot" className="h-2 w-2 rounded-full bg-red-500 shrink-0" />}
                      </span>
                      {it.body ? <span className="block text-xs text-muted-foreground line-clamp-2">{it.body}</span> : null}
                      <span className="block text-[11px] text-muted-foreground/70 mt-0.5">{rel(it.created_at)}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
